import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { contactInfo } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { toast } from 'sonner';
import { Seo } from '../components/Seo';
import { seoPages } from '../seo';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      toast.success(
        t({
          sl: 'Hvala! Vaše sporočilo je bilo poslano. Odgovorili vam bomo v najkrajšem možnem času.',
          en: 'Thank you! Your message has been sent. We will respond as soon as possible.',
          it: 'Grazie! Il vostro messaggio è stato inviato. Risponderemo il prima possibile.'
        })
      );
      setFormData({ name: '', email: '', phone: '', message: '' });
      setGdprConsent(false);
    } catch (error) {
      toast.error(
        t({
          sl: 'Napaka pri pošiljanju sporočila. Prosimo, poskusite znova.',
          en: 'Error sending message. Please try again.',
          it: 'Errore nell\'invio del messaggio. Si prega di riprovare.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactDetails = [
    {
      icon: MapPin,
      title: { sl: 'Naslov', en: 'Address', it: 'Indirizzo' },
      content: contactInfo.address
    },
    {
      icon: Phone,
      title: { sl: 'Telefon', en: 'Phone', it: 'Telefono' },
      content: contactInfo.phone,
      link: `tel:${contactInfo.phone}`
    },
    {
      icon: Mail,
      title: { sl: 'E-pošta', en: 'Email', it: 'Email' },
      content: contactInfo.email,
      link: `mailto:${contactInfo.email}`
    },
    {
      icon: Clock,
      title: { sl: 'Delovni čas', en: 'Business Hours', it: 'Orari di Lavoro' },
      content: contactInfo.hours
    }
  ];

  return (
    <div className="min-h-screen bg-[#000000] pt-32 pb-24">
      <Seo title={seoPages.contact.title} description={seoPages.contact.description} />
      <div className="container mx-auto px-5 md:px-10">
        <Reveal className="text-center mb-16">
          <h1 className="heading-2 text-[white] mb-6">
            {t({ sl: 'KONTAKTIRAJTE NAS', en: 'CONTACT US', it: 'CONTATTACI' })}
          </h1>
          <p className="body-large text-[#b0b0b0] max-w-3xl mx-auto">
            {t({
              sl: 'Imate vprašanje ali želite naročiti storitev? Stopite v stik z nami - z veseljem vam bomo pomagali!',
              en: 'Have a question or want to order a service? Get in touch - we will be happy to help you!',
              it: 'Avete domande o volete ordinare un servizio? Contattateci - saremo lieti di aiutarvi!'
            })}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <Reveal y={36} delay={0.1}>
          <Card className="bg-[#1a1a1a] border-[rgba(255, 255, 255, 0.1)] h-full">
            <CardHeader>
              <CardTitle className="text-[white] text-2xl">
                {t({ sl: 'Pošljite nam sporočilo', en: 'Send us a message', it: 'Inviateci un messaggio' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[white] mb-2 block">
                    {t({ sl: 'Ime in priimek', en: 'Full Name', it: 'Nome e Cognome' })} *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-[#000000] border-[rgba(255, 255, 255, 0.1)] text-[white] focus:border-[white]"
                    placeholder={t({ sl: 'Janez Novak', en: 'John Doe', it: 'Mario Rossi' })}
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[white] mb-2 block">
                    {t({ sl: 'E-pošta', en: 'Email', it: 'Email' })} *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-[#000000] border-[rgba(255, 255, 255, 0.1)] text-[white] focus:border-[white]"
                    placeholder="janez.novak@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[white] mb-2 block">
                    {t({ sl: 'Telefon', en: 'Phone', it: 'Telefono' })}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-[#000000] border-[rgba(255, 255, 255, 0.1)] text-[white] focus:border-[white]"
                    placeholder="+386 31 123 456"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-[white] mb-2 block">
                    {t({ sl: 'Sporočilo', en: 'Message', it: 'Messaggio' })} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="bg-[#000000] border-[rgba(255, 255, 255, 0.1)] text-[white] focus:border-[white] resize-none"
                    placeholder={t({
                      sl: 'Opišite vaš projekt ali vprašanje...',
                      en: 'Describe your project or question...',
                      it: 'Descrivete il vostro progetto o domanda...'
                    })}
                  />
                </div>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="gdpr"
                    data-testid="contact-gdpr-checkbox"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-1 border-gray-500 data-[state=checked]:bg-white data-[state=checked]:text-black"
                  />
                  <Label htmlFor="gdpr" className="text-[#b0b0b0] text-sm leading-relaxed cursor-pointer">
                    {t({
                      sl: <>Strinjam se z <Link to="/privacy-policy" className="text-white underline hover:text-gray-300">obdelavo osebnih podatkov</Link> za namen obravnave povpraševanja. *</>,
                      en: <>I agree to the <Link to="/privacy-policy" className="text-white underline hover:text-gray-300">processing of personal data</Link> for inquiry handling. *</>,
                      it: <>Acconsento al <Link to="/privacy-policy" className="text-white underline hover:text-gray-300">trattamento dei dati personali</Link> per la gestione della richiesta. *</>
                    })}
                  </Label>
                </div>
                <Button type="submit" className="btn-primary w-full" disabled={isSubmitting || !gdprConsent} data-testid="contact-submit-btn">
                  <Send className="mr-2 w-5 h-5" />
                  {isSubmitting 
                    ? t({ sl: 'Pošiljam...', en: 'Sending...', it: 'Invio in corso...' })
                    : t({ sl: 'Pošlji sporočilo', en: 'Send Message', it: 'Invia Messaggio' })
                  }
                </Button>
              </form>
            </CardContent>
          </Card>
          </Reveal>

          {/* Contact Details */}
          <Stagger className="space-y-6" stagger={0.12} data-testid="contact-details">
            {contactDetails.map((detail) => {
              const IconComponent = detail.icon;
              return (
                <StaggerItem key={detail.title.en}>
                <Card className="bg-[#1a1a1a] border-[rgba(255, 255, 255, 0.1)] team-card">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[white] flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-[#000000]" />
                    </div>
                    <div>
                      <h3 className="text-[white] font-semibold mb-2">
                        {t(detail.title)}
                      </h3>
                      {detail.link ? (
                        <a
                          href={detail.link}
                          className="text-[white] body-medium hover:text-[white] transition-colors"
                        >
                          {typeof detail.content === 'string'
                            ? detail.content
                            : t(detail.content)}
                        </a>
                      ) : (
                        <p className="text-[white] body-medium">
                          {typeof detail.content === 'string'
                            ? detail.content
                            : t(detail.content)}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>

        {/* Map */}
        <Reveal y={40}>
        <Card className="bg-[#1a1a1a] border-[rgba(255, 255, 255, 0.1)] overflow-hidden">
          <CardHeader>
            <CardTitle className="text-[white] text-2xl">
              {t({ sl: 'Najdite nas', en: 'Find Us', it: 'Trovateci' })}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="w-full h-[450px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2781.8976!2d13.7286!3d45.5481!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477b7a0a1a0a0a0a%3A0x0!2sUlica%2015.%20maja%2010%2C%206000%20Koper%2C%20Slovenia!5e0!3m2!1ssl!2ssi!4v1703000000000!5m2!1ssl!2ssi"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Matrix Print lokacija"
              />
            </div>
          </CardContent>
        </Card>
        </Reveal>
      </div>
    </div>
  );
};

export default Contact;
